// Auto-organização do fluxo em DUAS faixas, como um fluxo desenhado à mão:
// - FLUXO PRINCIPAL (Início → Proteções → Agente → … → Fim) em camadas da
//   esquerda pra direita; ramos (classify/if_else) empilham na vertical.
// - FERRAMENTAS (arestas saindo da porta 'tools') numa fileira horizontal
//   ABAIXO do nó pai, centralizadas nele — não entram nas camadas do fluxo.
// - Nós soltos (notas etc.) numa linha no rodapé.
// Função pura: retorna { id: {x,y} }.
const X_GAP = 300;
const Y_GAP = 140;
const X0 = 80;
const Y0 = 80;
const TOOL_DY = 190; // distância vertical do pai até a fileira de ferramentas
const TOOL_X_GAP = 230;
const TOOLS_PER_ROW = 4;

export function layoutPositions(nodes = [], edges = []) {
  const ids = new Set(nodes.map(n => n.id));
  const toolEdges = edges.filter(
    e => e.sourceHandle === 'tools' && ids.has(e.source) && ids.has(e.target)
  );
  const toolIds = new Set(toolEdges.map(e => e.target));

  // --- Fluxo principal: grafo SEM as ferramentas ---
  const flowEdges = edges.filter(
    e =>
      e.sourceHandle !== 'tools' &&
      ids.has(e.source) &&
      ids.has(e.target) &&
      !toolIds.has(e.source) &&
      !toolIds.has(e.target)
  );
  const adj = {};
  const indeg = {};
  nodes.forEach(n => {
    adj[n.id] = [];
    indeg[n.id] = 0;
  });
  const connected = new Set();
  flowEdges.forEach(e => {
    adj[e.source].push(e.target);
    indeg[e.target] += 1;
    connected.add(e.source);
    connected.add(e.target);
  });

  // Nível por ordenação topológica (Kahn) com caminho mais longo.
  const level = {};
  const indegCopy = { ...indeg };
  const queue = [...connected].filter(id => indeg[id] === 0);
  queue.forEach(id => {
    level[id] = 0;
  });
  let i = 0;
  while (i < queue.length) {
    const id = queue[i];
    i += 1;
    adj[id].forEach(t => {
      level[t] = Math.max(level[t] ?? 0, (level[id] ?? 0) + 1);
      indegCopy[t] -= 1;
      if (indegCopy[t] <= 0) queue.push(t);
    });
  }

  const byLevel = {};
  nodes.forEach(n => {
    if (!connected.has(n.id)) return;
    const lv = level[n.id] ?? 0;
    (byLevel[lv] = byLevel[lv] || []).push(n.id);
  });

  // Camadas ancoradas no topo (Y0): 1 nó no nível fica na linha do fluxo,
  // ramos descem a partir dela — o fluxo principal vira uma linha reta.
  const pos = {};
  let flowBottom = Y0;
  Object.entries(byLevel).forEach(([lv, group]) => {
    group.forEach((id, row) => {
      const y = Y0 + row * Y_GAP;
      pos[id] = { x: X0 + Number(lv) * X_GAP, y };
      flowBottom = Math.max(flowBottom, y);
    });
  });

  // --- Ferramentas: fileira(s) abaixo do PAI, centralizadas nele ---
  const toolsByParent = {};
  toolEdges.forEach(e => {
    (toolsByParent[e.source] = toolsByParent[e.source] || []).push(e.target);
  });
  let toolBottom = flowBottom;
  Object.entries(toolsByParent).forEach(([parent, tools]) => {
    const px = pos[parent]?.x ?? X0;
    const py = pos[parent]?.y ?? Y0;
    // dedup preservando ordem (uma ferramenta pode ter aresta duplicada)
    const unique = [...new Set(tools)];
    unique.forEach((id, idx) => {
      const row = Math.floor(idx / TOOLS_PER_ROW);
      const rowItems = Math.min(
        TOOLS_PER_ROW,
        unique.length - row * TOOLS_PER_ROW
      );
      const col = idx % TOOLS_PER_ROW;
      const x = px + (col - (rowItems - 1) / 2) * TOOL_X_GAP;
      // Fileira começa abaixo do fim do fluxo (não cobre ramos) e do pai.
      const y = Math.max(py, flowBottom) + TOOL_DY + row * (Y_GAP - 20);
      pos[id] = { x, y };
      toolBottom = Math.max(toolBottom, y);
    });
  });

  // --- Soltos (notas, nós sem aresta): linha do rodapé ---
  nodes
    .filter(n => !connected.has(n.id) && !toolIds.has(n.id))
    .forEach((n, idx) => {
      pos[n.id] = { x: X0 + idx * X_GAP, y: toolBottom + TOOL_DY };
    });

  return pos;
}
